import { EmpiricaClassic } from "@empirica/core/player/classic";
import {
  EmpiricaContext,
  useGame,
  usePlayer,
  useRound,
  useStage,
} from "@empirica/core/player/classic/react";
import {
  EmpiricaMenu,
  EmpiricaParticipant,
  PlayerCreate,
} from "@empirica/core/player/react";
import React from "react";
import { Game } from "./Game";
import { IntroSurvey } from "./intro-exit/IntroSurvey";
import { ExitSurvey } from "./intro-exit/ExitSurvey";
import { CompletionLink } from "./intro-exit/CompletionLink";
import { OutcomeStage } from "./intro-exit/OutcomeStage";
import { Consent } from "./intro-exit/Consent";
import { LlmAttentionCheck } from "./intro-exit/LlmAttentionCheck";
import {
  PartnerRemovedForInactivityStage,
  RemovedForInactivityStage,
} from "./intro-exit/InactivityExitStage";
import {
  QueueTimedOutStage,
  QueueWaitingStage,
} from "./intro-exit/QueueStages";
import { NegotiationOutcomeStage } from "./negotiation/NegotiationOutcomeStage";
import { Stage2ContributionQuestion } from "./stage2/Stage2ContributionQuestion";
import {
  CONTRIBUTION_AFTER_NEGOTIATION,
  shouldAskContribution,
} from "../../shared/contributionAssessment.js";

function OutcomeReviewFlow({ next }) {
  const player = usePlayer();
  const [view, setView] = React.useState("negotiation");

  function advanceFromNegotiation() {
    setView(shouldAskContribution(player, CONTRIBUTION_AFTER_NEGOTIATION)
      ? "contribution"
      : "final");
  }

  React.useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  }, [view]);

  if (view === "negotiation") {
    return <NegotiationOutcomeStage next={advanceFromNegotiation} />;
  }

  if (view === "contribution") {
    return (
      <Stage2ContributionQuestion
        timing={CONTRIBUTION_AFTER_NEGOTIATION}
        next={() => setView("final")}
      />
    );
  }

  return (
    <OutcomeStage
      back={() => setView("negotiation")}
      next={next}
    />
  );
}

function CompletionLinkAfterExitSurvey() {
  return <CompletionLink showFeedback={false} />;
}

function UnpaidCompletionLinkAfterExitSurvey() {
  return <CompletionLink unpaid showFeedback={false} />;
}

function LoadingExperiment() {
  const [serverUnavailable, setServerUnavailable] = React.useState(false);
  const [connectionTakingLong, setConnectionTakingLong] = React.useState(false);

  React.useEffect(() => {
    const serverUrl = import.meta.env.VITE_LLM_PROXY_URL;
    const connectionTimer = window.setTimeout(() => {
      setConnectionTakingLong(true);
    }, 15_000);

    if (!serverUrl) {
      return () => window.clearTimeout(connectionTimer);
    }

    const controller = new AbortController();
    const healthTimer = window.setTimeout(async () => {
      try {
        const response = await fetch(`${serverUrl.replace(/\/$/, "")}/health`, {
          signal: controller.signal,
        });
        if (!response.ok) {
          setServerUnavailable(true);
        }
      } catch (error) {
        if (error.name !== "AbortError") {
          setServerUnavailable(true);
        }
      }
    }, 10_000);

    return () => {
      window.clearTimeout(connectionTimer);
      window.clearTimeout(healthTimer);
      controller.abort();
    };
  }, []);

  if (serverUnavailable) {
    return (
      <div className="min-h-screen w-full px-6 py-10">
        <div className="mx-auto w-full max-w-md text-center">
          <h2 className="font-medium text-gray-700">
            The experiment is temporarily unavailable
          </h2>
          <p className="mt-2 text-gray-500">
            We could not finish starting your session. Please wait a moment and
            try again.
          </p>
          <button
            className="mt-5 rounded bg-gray-700 px-4 py-2 text-sm font-medium text-white hover:bg-gray-800"
            onClick={() => window.location.reload()}
            type="button"
          >
            Try again
          </button>
        </div>
      </div>
    );
  }

  if (connectionTakingLong) {
    return (
      <div className="min-h-screen w-full px-6 py-10">
        <div className="mx-auto w-full max-w-md text-center">
          <h2 className="font-medium text-gray-700">
            The study is taking longer than expected to connect
          </h2>
          <p className="mt-2 text-gray-500">
            If you use an ad or content blocker, temporarily disable it for this
            site, then try again. There are no ads, but it may interfere with
            our web services.
          </p>
          <button
            className="mt-5 rounded bg-gray-700 px-4 py-2 text-sm font-medium text-white hover:bg-gray-800"
            onClick={() => window.location.reload()}
            type="button"
          >
            Try again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen w-full items-start justify-center px-6 py-10 text-gray-700">
      Loading...
    </div>
  );
}

function NoGamesExperiment() {
  return (
    <div className="min-h-screen w-full px-6 py-10">
      <div className="mx-auto w-full max-w-md text-center">
        <h2 className="font-medium text-gray-700">
          No experiments available
        </h2>
        <p className="mt-2 text-gray-400">
          There are currently no available experiments. Please wait until an
          experiment becomes available or come back at a later date.
        </p>
      </div>
    </div>
  );
}

function FinishedExperiment() {
  return (
    <div className="min-h-screen w-full px-6 py-10">
      <div className="mx-auto w-full max-w-xl text-center">
        <h2 className="font-medium text-gray-700">Finished</h2>
        <p className="mt-2 text-gray-400">Thank you for participating</p>
      </div>
    </div>
  );
}

function GameOrQueue() {
  const game = useGame();
  const player = usePlayer();

  if (game) {
    return <Game />;
  }

  if (player?.get("introDone")) {
    return <QueueWaitingStage />;
  }

  return <LoadingExperiment />;
}

function PlayerCreateWithSessionReconnect({ connecting, onPlayerID }) {
  const registerAndReconnect = React.useCallback(
    async (participantIdentifier) => {
      await onPlayerID(participantIdentifier);
      window.location.reload();
    },
    [onPlayerID]
  );

  return (
    <PlayerCreate
      connecting={connecting}
      onPlayerID={registerAndReconnect}
    />
  );
}

function introSteps({ game, player }) {
  return [LlmAttentionCheck, Consent, IntroSurvey];
}

function exitSteps({ game, player }) {
  const participantExitStatus = player.get("participantExitStatus");

  if (participantExitStatus === "queue_timed_out") {
    return [QueueTimedOutStage];
  }

  const inactivityRemoval = game?.get("inactivityRemoval");

  if (
    inactivityRemoval?.removedPlayerIds?.includes(player.id) ||
    inactivityRemoval?.removedPlayerId === player.id ||
    participantExitStatus === "removed_for_inactivity"
  ) {
    return game
      ? [RemovedForInactivityStage]
      : [UnpaidCompletionLinkAfterExitSurvey];
  }

  if (
    inactivityRemoval?.partnerPlayerId === player.id ||
    participantExitStatus === "partner_removed_for_inactivity"
  ) {
    return game
      ? [PartnerRemovedForInactivityStage]
      : [CompletionLinkAfterExitSurvey];
  }

  if (!game) {
    return [ExitSurvey, CompletionLinkAfterExitSurvey];
  }

  return [OutcomeReviewFlow, ExitSurvey, CompletionLinkAfterExitSurvey];
}

function ManualExitFlow() {
  const game = useGame();
  const player = usePlayer();

  if (!player) {
    return <LoadingExperiment />;
  }

  const steps = exitSteps({ game, player });
  const index = Number(player.get("exitStep") || 0);
  const done = player.get("exitStepDone") === true;
  const Step = steps[index];

  function next() {
    if (index + 1 >= steps.length) {
      player.set("exitStepDone", true);
      return;
    }

    player.set("exitStep", index + 1);
  }

  function previous() {
    if (index > 0) {
      player.set("exitStep", index - 1);
    }
  }

  if (done || !Step) {
    return <FinishedExperiment />;
  }

  return <Step index={index} previous={previous} next={next} />;
}

function ExperimentContext() {
  const player = usePlayer();

  // Duplicate cleanup can mark the currently selected scope as ended before
  // its unlink reaches the browser. Wait for the retained scope instead of
  // sending a participant who has not played a game into the exit flow.
  if (
    player?.get("participantExitStatus") === "duplicate_player_scope" ||
    player?.get("removalReason") === "duplicate_player_scope"
  ) {
    return <LoadingExperiment />;
  }

  if (player?.get("ended")) {
    return <ManualExitFlow />;
  }

  return (
    <EmpiricaContext
      connecting={LoadingExperiment}
      introSteps={introSteps}
      exitSteps={exitSteps}
      finished={FinishedExperiment}
      loading={LoadingExperiment}
      noGames={NoGamesExperiment}
      playerCreate={PlayerCreateWithSessionReconnect}
      unmanagedGame
    >
      <GameOrQueue />
    </EmpiricaContext>
  );
}

function ScrollToTopOnPageChange({ scrollerRef }) {
  const game = useGame();
  const player = usePlayer();
  const round = useRound();
  const stage = useStage();

  const pageKey = [
    player?.id || "no-player",
    player?.get("introDone") === true ? "intro-done" : "intro-active",
    player?.get("intro") || 0,
    player?.get("ended") ? "ended" : "active",
    player?.get("exitStepDone") === true ? "exit-done" : "exit-active",
    player?.get("exitStep") || 0,
    player?.get("participantExitStatus") || "no-exit-status",
    game?.id || "no-game",
    game?.hasEnded ? "game-ended" : "game-active",
    game?.get("status") || "no-game-status",
    round?.id || round?.get("name") || "no-round",
    stage?.id || stage?.get("name") || "no-stage",
  ].join("|");

  React.useLayoutEffect(() => {
    const scroller = scrollerRef.current;
    if (!scroller) {
      return undefined;
    }

    function scrollToTop() {
      scroller.scrollTo({ top: 0, left: 0, behavior: "auto" });
    }

    scrollToTop();
    const frame = window.requestAnimationFrame(scrollToTop);

    return () => {
      window.cancelAnimationFrame(frame);
    };
  }, [pageKey, scrollerRef]);

  return null;
}

export default function App() {
  const scrollerRef = React.useRef(null);
  const urlParams = new URLSearchParams(window.location.search);
  const providedKey = urlParams.get("participantKey");
  const sessionKey = sessionStorage.getItem("participantKey");
  const playerKey =
    providedKey ||
    sessionKey ||
    (() => {
      const generated =
        typeof crypto !== "undefined" && crypto.randomUUID
          ? `p_${crypto.randomUUID()}`
          : `p_${Date.now()}_${Math.random().toString(16).slice(2)}`;
      sessionStorage.setItem("participantKey", generated);
      return generated;
    })();

  const { protocol, host } = window.location;
  const url = import.meta.env.VITE_TAJRIBA_URL || `${protocol}//${host}/query`;

  return (
    <EmpiricaParticipant url={url} ns={playerKey} modeFunc={EmpiricaClassic}>
      <div className="h-screen relative">
        <EmpiricaMenu position="bottom-left" />
        <div ref={scrollerRef} className="h-full overflow-auto">
          <ScrollToTopOnPageChange scrollerRef={scrollerRef} />
          <ExperimentContext />
        </div>
      </div>
    </EmpiricaParticipant>
  );
}
